import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { WebsiteContentProvider } from "@/contexts/WebsiteContentContext";
import { PreviewProvider } from "@/contexts/PreviewContext";
import { ServerErrorProvider } from "@/contexts/ServerErrorContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FeatureErrorBoundary } from "@/components/FeatureErrorBoundary";
import MockServerIndicator from "@/components/MockServerIndicator";

// Eager load landing page for fast initial render
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import ProtectedRoute from "@/components/ProtectedRoute";

// Lazy load auth pages from feature folder
const Login = lazy(() => import("@/features/auth/pages/LoginPage"));
const RegistrationSuccess = lazy(() => import("@/features/auth/pages/RegistrationSuccessPage"));
const ResetPassword = lazy(() => import("@/features/auth/pages/ResetPasswordPage"));
const SessionTimeout = lazy(() => import("@/features/auth/pages/SessionTimeoutPage"));
// Lazy load publisher pages from feature folder
const PublisherDashboardPage = lazy(() => import("@/features/publisher/pages/PublisherDashboardPage"));
const UploadBooksMetaDataPage = lazy(() => import("@/features/publisher/pages/UploadBooksMetaDataPage"));
const ContractRequestPage = lazy(() => import("@/features/publisher/pages/ContractRequestPage"));
// Lazy load profile page from feature folder
const ProfilePage = lazy(() => import("@/features/profile/pages/ProfilePage"));

// Lazy load CMS pages from feature folder
const CMSLayout = lazy(() => import("@/features/cms/components/CMSLayout"));
const CMSDashboardPage = lazy(() => import("@/features/cms/pages/CMSDashboardPage"));
const WebsiteContentPage = lazy(() => import("@/features/cms/pages/WebsiteContentPage"));
const CMSSettingsPage = lazy(() => import("@/features/cms/pages/CMSSettingsPage"));

// Lazy load ALC pages from feature folders
const ALCOverviewDashboardPage = lazy(() => import("@/features/alc-dashboard/pages/ALCOverviewDashboardPage"));
const ALCBookReviewerDashboardPage = lazy(() => import("@/features/alc-book-reviewer/pages/ALCBookReviewerDashboardPage"));
const ALCManagerDashboardPage = lazy(() => import("@/features/alc-manager/pages/ALCManagerDashboardPage"));

// Lazy load Arabookverse contracts dashboard
const ArabookverseContractsDashboardPage = lazy(() => import("@/features/arabookverse-contracts/pages/ArabookverseContractsDashboardPage"));

// Lazy load Content Rating page
const ContentRatingTasksPage = lazy(() => import("@/features/content-rating/pages/ContentRatingTasksPage"));

// Loading fallback component
import { PageSkeleton } from "@/components/ui/page-skeleton";
const PageLoader = () => <PageSkeleton />;

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <WebsiteContentProvider>
          <PreviewProvider>
            <ServerErrorProvider>
            <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <MockServerIndicator position="top-right" compact />
              <BrowserRouter>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/registration-success" element={<RegistrationSuccess />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/session-timeout" element={<SessionTimeout />} />
                    <Route path="/publisher/dashboard" element={<FeatureErrorBoundary featureName="Publisher Dashboard"><PublisherDashboardPage /></FeatureErrorBoundary>} />
                    <Route path="/publisher/uploadMetaData" element={<FeatureErrorBoundary featureName="Upload Books"><UploadBooksMetaDataPage /></FeatureErrorBoundary>} />
                    <Route path="/publisher/contract" element={<FeatureErrorBoundary featureName="Contract Request"><ContractRequestPage /></FeatureErrorBoundary>} />
                    <Route path="/profile" element={<FeatureErrorBoundary featureName="Profile"><ProfilePage /></FeatureErrorBoundary>} />

                    {/* CMS Routes - Requires ARABOOKVERSE_BACKOFFICE or ADMIN role */}
                    <Route path="/cms" element={<ProtectedRoute requiredRole="ARABOOKVERSE_BACKOFFICE"><FeatureErrorBoundary featureName="CMS"><CMSLayout /></FeatureErrorBoundary></ProtectedRoute>}>
                      <Route path="dashboard" element={<CMSDashboardPage />} />
                      <Route path="website-content" element={<WebsiteContentPage />} />
                      <Route path="settings" element={<CMSSettingsPage />} />
                    </Route>

                    {/* ALC Backoffice Routes */}
                    <Route path="/alc/overview" element={<FeatureErrorBoundary featureName="ALC Overview"><ALCOverviewDashboardPage /></FeatureErrorBoundary>} />
                    <Route path="/alc/book-reviewer" element={<FeatureErrorBoundary featureName="Book Reviewer"><ALCBookReviewerDashboardPage /></FeatureErrorBoundary>} />
                    <Route path="/alc/manager" element={<FeatureErrorBoundary featureName="ALC Manager"><ALCManagerDashboardPage /></FeatureErrorBoundary>} />
                    <Route path="/alc/contracts" element={<FeatureErrorBoundary featureName="Contract Review"><ArabookverseContractsDashboardPage /></FeatureErrorBoundary>} />
                    <Route path="/alc/content-rating/tasks" element={<FeatureErrorBoundary featureName="Content Rating"><ContentRatingTasksPage /></FeatureErrorBoundary>} />

                    {/* Legacy redirects — old standalone contract routes consolidated into owning module pages */}
                    <Route path="/publisher/contracts" element={<Navigate to="/publisher/contract" replace />} />
                    <Route path="/backoffice/contracts" element={<Navigate to="/alc/contracts" replace />} />

                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </TooltipProvider>
            </AuthProvider>
            </ServerErrorProvider>
          </PreviewProvider>
        </WebsiteContentProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
